import { lazy, Suspense, useEffect, useState } from "react";

/**
 * Loads the Three.js / Rapier lanyard scene only where it can pay for itself.
 *
 * The scene pulls in Three.js, React Three Fiber, a WASM physics engine, and
 * meshline — several hundred KB that a phone would pay for in load time and
 * battery just to render nothing, since there is no hover to react to and
 * dragging a physics object would fight the page's own scroll. Gating on
 * `(hover: hover)` rather than a width breakpoint is deliberate: it excludes
 * touch devices at any screen size, including large tablets, rather than
 * excluding by pixel count and guessing wrong on a big phone in landscape.
 *
 * `import()` only fires after the check passes, so the code itself never
 * reaches a phone's network tab.
 */
const Lanyard = lazy(() => import("./Lanyard"));

interface LanyardGateProps {
    frontImage?: string;
}

export default function LanyardGate({ frontImage }: LanyardGateProps) {
    const [canRender, setCanRender] = useState(false);

    useEffect(() => {
        const mq = window.matchMedia("(hover: hover) and (pointer: fine)");
        setCanRender(mq.matches);
        const onChange = (e: MediaQueryListEvent) => setCanRender(e.matches);
        mq.addEventListener("change", onChange);
        return () => mq.removeEventListener("change", onChange);
    }, []);

    /*
     * Mount the 3D scene only once the hero's own entrance has played and the
     * browser has a quiet moment: loading Three.js and Rapier and building the
     * scene is heavy, and doing it mid-entrance made the hero stutter. The
     * card then drops in on its own, after the text.
     */
    const [settled, setSettled] = useState(false);
    useEffect(() => {
        if (!canRender) return;
        let idle = 0;
        const t = window.setTimeout(() => {
            if ("requestIdleCallback" in window) idle = window.requestIdleCallback(() => setSettled(true), { timeout: 1500 });
            else setSettled(true);
        }, 900);
        return () => {
            window.clearTimeout(t);
            if (idle && "cancelIdleCallback" in window) window.cancelIdleCallback(idle);
        };
    }, [canRender]);

    if (!canRender || !settled) return null;

    return (
        <Suspense fallback={null}>
            <Lanyard frontImage={frontImage} transparent />
        </Suspense>
    );
}
